import { useState } from 'react';
import { IStopArea } from '../../../../../../../models/stop-area.model';
import { t } from '../../../../../../../services/texts.service';
import { getDefinedStopAreas } from '../../../../helpers/settings.helper';
import { StopAreaSlots } from '../../../../models/settings.model';
import FocusTrap from '../../../../../../Shared/FocusTrap';
import { Icon } from '../../../../../../Shared/Icon';
import { IconColor } from '../../../../../../Shared/Icon/icon-color.enum';
import { IconMotif } from '../../../../../../Shared/Icon/icon-motif.enum';
import { IconVariant } from '../../../../../../Shared/Icon/icon-variant.enum';
import { StopAreaType } from '../../../../models/stop-area-type.enum';
import LocationAutocomplete, { ILocationAutocompleteSelection, ILocationAutocompleteState } from '../../../../../../Shared/LocationAutocomplete';
import { LocationType } from '../../../../../../../models/location-type.enum';
import ValidationInfo from '../../../../../../../validation/validation-info';
import { AutocompleteLabelPosition } from '../../../../../../Shared/AutocompleteBase/autocomplete-base.enum';
import { LocationAutocompleteSelectionType } from '../../../../../../Shared/LocationAutocomplete/location-autocomplete-selection-type.enum';

function convertStopAreaToLocationAutocompleteSelection(stopArea: IStopArea | undefined): ILocationAutocompleteSelection | undefined {
  return !stopArea ? undefined : { type: LocationAutocompleteSelectionType.location, location: { ...stopArea, locationType: LocationType.stopArea, hasLocalService: false } };
}

export default function SelectStopAreas({
  type,
  stopAreas,
  className,
  onSave,
  singleStopArea = false,
}: {
  type: StopAreaType;
  stopAreas: StopAreaSlots;
  className?: string;
  onSave: (stopAreas: StopAreaSlots) => void;
  singleStopArea?: boolean;
}) {
  // === STATE ===
  const [showSelectModal, setShowSelectModal] = useState<boolean>(false);
  const [editingIndex, setEditingIndex] = useState<number | undefined>(undefined);
  const [locationAutocompleteState, setLocationAutocompleteState] = useState<ILocationAutocompleteState>({
    value: undefined,
    validationInfo: new ValidationInfo(),
  });

  // === VARIABLES ===
  const modalTitle = `${type}-modal-title`;
  const allSelectedStopAreas = stopAreas.map((stopArea, index) => ({ stopArea, index })).filter((item): item is { stopArea: IStopArea; index: number } => item.stopArea !== undefined);
  const selectedStopAreas = singleStopArea ? allSelectedStopAreas.slice(0, 1) : allSelectedStopAreas;
  let stopAreaTypeText = t('Okänd', 'Unknown');
  if (type === StopAreaType.Home) {
    stopAreaTypeText = t('Hem', 'Home');
  } else if (type === StopAreaType.Work) {
    stopAreaTypeText = t('Arbete', 'Work');
  }

  // === EVENT HANDLERS ===
  function openModal(index: number): void {
    setEditingIndex(index);
    setLocationAutocompleteState({
      value: convertStopAreaToLocationAutocompleteSelection(stopAreas[index]),
      validationInfo: new ValidationInfo(),
    });
    setShowSelectModal(true);
  }

  function handleLocationSelect(item: ILocationAutocompleteSelection | undefined): void {
    setLocationAutocompleteState({ value: item, validationInfo: new ValidationInfo() });
  }

  function handleOnClose(): void {
    setLocationAutocompleteState({
      value: editingIndex === undefined ? undefined : convertStopAreaToLocationAutocompleteSelection(stopAreas[editingIndex]),
      validationInfo: new ValidationInfo(),
    });
    setEditingIndex(undefined);
    setShowSelectModal(false);
  }

  function handleSave(): void {
    if (editingIndex === undefined) return;

    const updatedStopAreas = [...stopAreas] as StopAreaSlots;
    setShowSelectModal(false);
    if (locationAutocompleteState.value?.location) {
      updatedStopAreas[editingIndex] = {
        gid: locationAutocompleteState.value.location.gid!,
        name: locationAutocompleteState.value.location.name!,
        latitude: locationAutocompleteState.value.location.latitude,
        longitude: locationAutocompleteState.value.location.longitude,
        stopPoints: [],
      };
    }

    setEditingIndex(undefined);
    onSave(updatedStopAreas);
  }

  function handleRemoveStopArea(index: number): void {
    const updatedStopAreas = [...stopAreas] as StopAreaSlots;
    updatedStopAreas[index] = undefined;
    onSave(updatedStopAreas);
  }

  function handleAddStopArea(): void {
    const emptyIndex = stopAreas.findIndex((stopArea) => stopArea === undefined);
    if (emptyIndex >= 0) openModal(emptyIndex);
  }

  // === RENDERING ===
  return (
    <>
      {showSelectModal && (
        <>
          <FocusTrap isVisible={showSelectModal} focusOnFirstElement={true} onEscape={handleOnClose}>
            <div className="modal d-block fade show" aria-labelledby={modalTitle} aria-modal="true" role="dialog">
              <div className="modal-dialog">
                <div className="modal-content">
                  <div className="modal-header">
                    <h3 id={modalTitle} className="modal-title">
                      {t.stopArea}
                    </h3>
                  </div>
                  <div className="modal-body">
                    <LocationAutocomplete
                      id={`${type}-autocomplete`}
                      locationTypes={[LocationType.stopArea]}
                      state={locationAutocompleteState}
                      labelText={t('Välj', 'Select') + ':'}
                      labelPosition={AutocompleteLabelPosition.inside}
                      onSelect={handleLocationSelect}
                    ></LocationAutocomplete>
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="btn btn-secondary" onClick={handleOnClose}>
                      {t.cancel}
                    </button>
                    <button type="button" className="btn btn-primary" onClick={handleSave}>
                      {t('Spara val', 'Save selection')}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </FocusTrap>
          <div className="modal-backdrop fade show" />
        </>
      )}
      <div className={`border border-gray-200 bg-white rounded ${className ?? ''}`}>
        <div className="px-3 py-2 bg-blue d-flex justify-content-between rounded align-items-center">
          <h3 className="h4 mb-0">{stopAreaTypeText}</h3>
        </div>
        <div>
          {selectedStopAreas.length > 0 && (
            <div className="d-flex flex-column">
              {selectedStopAreas.map(({ stopArea, index }) => {
                return (
                  <div key={`${stopArea.gid}-${index}`} className="border-top border-bottom border-gray-200 py-2 px-3 d-flex justify-content-between align-items-center gap-2">
                    <div className="d-flex align-items-center gap-1">
                      <Icon motif={IconMotif.StopArea} variant={IconVariant.Circle} altText="" />
                      <span>{stopArea.name}</span>
                    </div>
                    <div className="d-flex align-self-center border border-gray-200 rounded overflow-hidden bg-white" role="group">
                      {selectedStopAreas.length > 1 && (
                        <button
                          type="button"
                          className="btn--unstyled btn--outline-internal d-flex align-items-center justify-content-center p-2 border-end border-gray-200"
                          aria-label={`${t('Ta bort hållplats', 'Remove stop area')}: ${stopArea.name}`}
                          onClick={() => handleRemoveStopArea(index)}
                        >
                          <Icon motif={IconMotif.Trash} color={IconColor.Blue1000} altText="" />
                        </button>
                      )}
                      <button
                        type="button"
                        className="btn--unstyled btn--outline-internal d-flex align-items-center justify-content-center p-2"
                        aria-label={`${t('Ändra hållplats', 'Change stop area')}: ${stopArea.name}`}
                        onClick={() => openModal(index)}
                      >
                        <Icon motif={IconMotif.Pen} color={IconColor.Blue1000} altText="" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          {selectedStopAreas.length === 0 && (
            <div className="p-3">
              <button
                type="button"
                className="btn btn-secondary w-100"
                onClick={() => {
                  openModal(0);
                }}
              >
                {t('Välj hållplats', 'Select stop area')}
              </button>
            </div>
          )}
          {selectedStopAreas.length > 0 && !singleStopArea && getDefinedStopAreas(stopAreas).length < stopAreas.length && (
            <div className="px-3">
              <button type="button" className="btn btn-tertiary d-inline-flex align-items-center gap-1" onClick={handleAddStopArea}>
                <Icon motif={IconMotif.Plus} color={IconColor.Blue1000} altText="" />
                <span>{t('Lägg till hållplats', 'Add stop area')}</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
